import {
	ChangeDetectionStrategy,
	Component,
	WritableSignal,
	signal,
	OnInit,
	inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Vflow, Node, Edge, Connection, ComponentNodeEvent } from 'ngx-vflow';
import { NodeEditorService } from './node-editor.service';
import { NodeService } from '../node-service/node.service';
import { GameData } from '@meta-press/shared-plugins';
import { GameDataNodeComponent } from './components/game-data-node.component';

@Component({
	selector: 'app-node-editor',
	imports: [CommonModule, Vflow],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	templateUrl: './node-editor.component.html',
	styleUrl: './node-editor.component.scss',
})
export class NodeEditorComponent implements OnInit {
	private nodeEditorService = inject(NodeEditorService);
	private nodeService = inject(NodeService);

	public nodes: WritableSignal<Node[]> = signal<Node[]>([]);
	public edges: WritableSignal<Edge[]> = signal<Edge[]>([]);

	ngOnInit(): void {
		const currentGameData = this.nodeEditorService.currentGameData;
		if (currentGameData) {
			this.createGraphFromGameData(currentGameData);
		}
	}

	private createGraphFromGameData(gameData: GameData): void {
		// Option 1: Using GameDataIndexes (recommended for better control)
		const indexes = gameData.GetGameDataIndexesRecursively();
		const nodes = this.nodeService.createNodesFromGameDataIndexes(indexes);
		const edges = this.nodeService.createEdgesFromGameDataIndexes(indexes);

		// Option 2: Using GameDataTree (alternative approach)
		// const tree = gameData.GetGameDataTree();
		// const nodes = this.nodeService.createNodesFromGameDataTree(tree);
		// const edges = this.nodeService.createEdgesFromGameDataTree(tree);

		this.nodes.set(nodes);
		this.edges.set(edges);
	}

	handleConnection(connection: Connection) {
		this.createEdge(connection);
	}

	handleComponentEvent(event: ComponentNodeEvent<[GameDataNodeComponent]>) {
		if (event.eventName === 'nodeClicked') {
			console.log('Node clicked:', event.eventPayload);
			// Handle node click event here
			// You can add your custom logic for when a game data node is clicked
		}

		if (event.eventName === 'gearClicked') {
			console.log('Gear clicked, updated game data:', event.eventPayload);
			// Handle gear click event here
			// The event.eventPayload contains the updated gameData and path
			// You can update your data structure or notify other components
			this.handleGameDataUpdate(event.eventPayload);
		}
	}

	private handleGameDataUpdate(payload: {
		gameData: GameData;
		path: number[];
	}): void {
		// Update the game data in your data structure
		// This depends on how your data is organized
		const currentGameData = this.nodeEditorService.currentGameData;
		if (currentGameData) {
			// You might want to update the specific node's data here
			// For now, we'll just log it
			console.log(
				'Updating game data at path:',
				payload.path,
				'with new data:',
				payload.gameData,
			);

			// Optionally refresh the graph to show any changes
			this.createGraphFromGameData(currentGameData);
		}
	}

	createEdge({
		source,
		target,
		sourceHandle,
		targetHandle,
	}: Connection): void {
		this.edges.update((edges) => [
			...edges,
			{
				id: `${source}->${target}${sourceHandle ?? ''}${targetHandle ?? ''}`,
				source,
				target,
				sourceHandle,
				targetHandle,
				data: { color: '#64748b' },
				markers: { end: { type: 'arrow-closed' } },
			},
		]);
	}
}
